import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";

/**
 * Plausible Analytics loader — pure React component that injects the Plausible
 * script lazily. Reads `REACT_APP_PLAUSIBLE_DOMAIN` from the environment;
 * if unset, this is a complete no-op (so dev environments stay clean).
 *
 * Usage: render once in App.js. Safe to mount multiple times — it short-circuits
 * if the script is already in the DOM.
 *
 * Custom events: anywhere in the app, call `window.plausible?.(eventName, opts)`.
 * We auto-track these conversion goals via attribute-tagged buttons in the markup:
 *   • Signup
 *   • Login
 *   • Checkout: Personal | Professional | Topup
 *   • Export CV
 *   • Cover Letter
 *
 * Why Plausible and not GA4: GDPR-compliant by default, no cookie banner needed,
 * 1KB script (vs ~50KB for GA4), and the dashboard is calmer for a launch context.
 */
export const PlausibleLoader = () => {
  const domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN; // e.g. "prestiqueresume.com"
  const apiHost = process.env.REACT_APP_PLAUSIBLE_HOST || "https://plausible.io";

  useEffect(() => {
    if (!domain) return;                          // no-op in dev / unset envs
    if (document.querySelector("script[data-plausible]")) return;  // already mounted

    const script = document.createElement("script");
    script.defer = true;
    script.dataset.domain = domain;
    // tagged-events plugin lets us track conversions via data-attributes on CTAs
    script.src = `${apiHost}/js/script.tagged-events.outbound-links.js`;
    script.setAttribute("data-plausible", "true");
    document.head.appendChild(script);

    // Lightweight stub so calls don't throw before the script loads
    if (!window.plausible) {
      window.plausible = function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
    }
  }, [domain, apiHost]);

  return null;
};

/**
 * Programmatic event helper. Call from anywhere:
 *   trackEvent("Signup", { props: { method: "email" } });
 */
export const trackEvent = (name, opts) => {
  if (typeof window !== "undefined" && typeof window.plausible === "function") {
    try { window.plausible(name, opts); } catch { /* never break the app */ }
  }
};

/**
 * First-party pageview tracker — fires on every route change, writes to
 * /api/analytics/pageview so the Admin dashboard can render visit / conversion
 * stats without depending on Plausible being configured.
 *
 * Uses sessionStorage to mint a stable session_id for the tab so we can count
 * unique visitors.
 */
const SESSION_KEY = "pq_session_id";

const getSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
};

export const PageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    // Skip the auth-callback fragment route — that's an internal redirect, not a page.
    if (location.pathname === "/auth/callback") return;
    api.post("/analytics/pageview", {
      path: location.pathname,
      referrer: document.referrer || "",
      session_id: getSessionId(),
    }).catch(() => { /* never break the app */ });
  }, [location.pathname]);
  return null;
};
